import { quantile } from './stats';
import type { PreprocessOptions, PreprocessReport, ScoredTrial } from './types';

export const DEFAULT_PREPROCESS: PreprocessOptions = {
  absoluteBoundsMs: [150, 1500],     // Pond et al. 2025 (Hazen 2009 따름)
  dropFirstNPerBlock: 3,             // Price et al. 2015
  winsorize: true,                   // Price et al. 2015 권고
  maxRemovedFraction: 0.10,          // Pond et al. 2025
};

export interface PreprocessOutput {
  /** 분석에 쓰는 시행. rt 는 윈저화된 값. */
  valid: ScoredTrial[];
  report: PreprocessReport;
}

/**
 * RT 전처리. 순서가 중요하다:
 *  1. 블록별 원래 첫 N 시행 제거 (이후 범주와 중복 집계하지 않음)
 *  2. aborted / timeout / 오류 제거
 *  3. 절대 범위 밖 제거
 *  4. 선형 보간 사분위수의 1.5 IQR 울타리로 윈저화 (원자료 보존)
 *  5. 제거 비율이 임계 초과면 참가자 제외 플래그
 */
export function preprocess(trials: readonly ScoredTrial[], opts: PreprocessOptions = DEFAULT_PREPROCESS): PreprocessOutput {
  const total = trials.length;
  let removedError = 0, removedTimeout = 0, removedAborted = 0, removedBounds = 0, removedFirstN = 0;

  // 블록별 첫 N
  const seenPerBlock = new Map<string, number>();
  const stage1: ScoredTrial[] = [];
  for (const t of [...trials].sort((a, b) => a.trialIndex - b.trialIndex)) {
    const k = seenPerBlock.get(t.blockId) ?? 0;
    seenPerBlock.set(t.blockId, k + 1);
    if (k < opts.dropFirstNPerBlock) { removedFirstN += 1; continue; }
    if (t.aborted) { removedAborted += 1; continue; }
    if (t.timedOut) { removedTimeout += 1; continue; }
    if (t.correct !== true || t.rt === null) { removedError += 1; continue; }
    const [lo, hi] = opts.absoluteBoundsMs;
    if (!Number.isFinite(t.rt) || t.rt < lo || t.rt > hi) { removedBounds += 1; continue; }
    stage1.push(t);
  }

  let winsorized = 0;
  let winsorBounds: [number, number] | null = null;
  let valid = stage1;
  if (opts.winsorize && stage1.length >= 8) {
    const rts = stage1.map((t) => t.rt as number);
    const q1 = quantile(rts, 0.25), q3 = quantile(rts, 0.75), iqr = q3 - q1;
    const lo = q1 - 1.5 * iqr, hi = q3 + 1.5 * iqr;
    // "범위 내 마지막 유효값으로 재할당" — 범위 안에 실제로 존재하는 최소/최대값
    const inside = rts.filter((x) => x >= lo && x <= hi);
    const clampLo = Math.min(...inside), clampHi = Math.max(...inside);
    winsorBounds = [clampLo, clampHi];
    valid = stage1.map((t) => {
      const rt = t.rt as number;
      if (rt < clampLo) { winsorized += 1; return { ...t, rt: clampLo }; }
      if (rt > clampHi) { winsorized += 1; return { ...t, rt: clampHi }; }
      return t;
    });
  }

  const removed = removedError + removedTimeout + removedAborted + removedBounds + removedFirstN;
  const removedFraction = total === 0 ? 0 : removed / total;
  return {
    valid,
    report: {
      total, removedError, removedTimeout, removedAborted, removedBounds, removedFirstN, winsorized,
      removedFraction,
      participantExcluded: removedFraction > opts.maxRemovedFraction,
      winsorBounds,
    },
  };
}
